import React                                                                     from 'react';
import { CorvalCategory, CorvalContextOwner, CorvalStaticProjectActivity, Uuid } from '../types';
import _                                                                         from 'underscore';
import { useApolloClient }                                                       from "@apollo/react-hooks";

import { CATEGORY_INFO, CategoryInfoItem, ENTITY_COLORIZATION, ENTITY_ICONS } from '@src/constants';
import { runIfFunction }                                                      from "@src/util";
import { useSystemModals }                                                    from "@src/hooks/useSystem";
import { AddEmptyProjectTaxonomyDialog, AddEmptyProjectTaxonomyDialogProps }  from "@src/views/pages/projects/operations/AddEmptyProjectTaxonomyDialog";
import { AddNewProjectTaxonomyDialog, AddNewProjectTaxonomyDialogProps }      from "@src/views/pages/projects/operations/AddNewProjectTaxonomyDialog";
import { UpdateProjectTaxonomyDialog, UpdateProjectTaxonomyDialogProps }      from "@src/views/pages/projects/operations/UpdateProjectTaxonomyDialog";
import { DecisionEngineQueries }                                              from "@src/queries";
import { ActivityQueries }                                                    from "@src/queries/ActivityQueries";


export interface UseCategoriesHook {

    /* Returns an icon and color set based on the name of a category; ducktyped by name */
    findCategoryDisplayInfo( category: CorvalCategory ): Pick<CategoryInfoItem, 'ICON' | 'COLOR'> | null,

    findAllTaxonomyNames( type: 'category' | 'subCategory', projectId: Uuid, projectVersionId: Uuid ): Promise<string[]>,

    findAllCategoryNames( projectId: Uuid, projectVersionId: Uuid ): Promise<string[]>,

    findAllSubCategoryNames( projectId: Uuid, projectVersionId: Uuid ): Promise<string[]>,

    addEmptyProjectTaxonomy(
        projectId?: Uuid,
        projectVersionId?: Uuid,
        excludedTaxonomyIds?: Uuid[],
        categoryId?: Uuid,
        onComplete?: AddEmptyProjectTaxonomyDialogProps['onComplete']
    ): void,

    addNewProjectTaxonomy(
        projectId?: Uuid,
        projectVersionId?: Uuid,
        categoryId?: Uuid,
        onComplete?: AddNewProjectTaxonomyDialogProps['onComplete']
    ): void,

    updateProjectTaxonomy( props: UpdateProjectTaxonomyDialogProps ): void,
}

function useCategories(): UseCategoriesHook{

    const modalActions = useSystemModals(),
          apolloClient = useApolloClient();

    const findCategoryDisplayInfo: UseCategoriesHook['findCategoryDisplayInfo'] = ( category ) => {

        let categoryHash = _.values( CATEGORY_INFO );

        let findByDisplayName = _.findWhere( categoryHash, { LABEL: category.displayName } ) ?? null,
            findByName        = _.findWhere( categoryHash, { LABEL: category.name } ) ?? null;

        let displayInfo = findByDisplayName ?? findByName ?? {
            ICON:  ENTITY_ICONS.CATEGORIES.GENERIC,
            COLOR: ENTITY_COLORIZATION.CATEGORIES.GENERIC,
        };

        return _.pick( displayInfo, [ 'ICON', 'COLOR' ] );
    }

    const findAllCategoryNames: UseCategoriesHook['findAllCategoryNames'] = async ( projectId, projectVersionId ) => {

        return findAllTaxonomyNames( 'category', projectId, projectVersionId );
    }

    const findAllSubCategoryNames: UseCategoriesHook['findAllCategoryNames'] = async ( projectId, projectVersionId ) => {

        return findAllTaxonomyNames( 'subCategory', projectId, projectVersionId );
    }

    const findAllTaxonomyNames: UseCategoriesHook['findAllTaxonomyNames'] = async ( type, projectId, projectVersionId ) => {

        if( !projectId || !projectVersionId ) return [];

        // Get all DEM subcategories that are available
        const getAvailableCategoryReq = await apolloClient.query<{ categories: CorvalCategory[] }>( {
            query:       DecisionEngineQueries.queries.GET_CATEGORIES( `id name displayName ordinal description subCategories{ id name displayName ordinal description }` ),
            variables:   {
                where: {
                    or: [
                        { contextOwner: { eq: CorvalContextOwner.Corval } },
                        { contextOwnerProjectId: { eq: projectId } }
                    ]
                }
            },
            fetchPolicy: 'network-only'
        } );

        // Get all static project activities for the given project version and the taxonomy info therein
        const getStaticProjectActivitiesReq = await apolloClient.query<{ staticProjectActivites: CorvalStaticProjectActivity[] }>( {
            query:       ActivityQueries.queries.GET_PROJECT_ACTIVITIES( `id categoryId categoryName subCategoryId subCategoryName`, projectVersionId ),
            variables:   { projectVersionId },
            fetchPolicy: 'network-only'
        } );

        const nameStrings = new Set();

        if( type === 'subCategory' ){

            getAvailableCategoryReq.data.categories.map( cat => cat.subCategories ).flat().map( subCat => {
                nameStrings.add( subCat.name );
                nameStrings.add( subCat.displayName );
            } );

            getStaticProjectActivitiesReq.data.staticProjectActivites.map( activity => {
                nameStrings.add( activity.subCategoryName );
            } )

            // return _.uniq( [
            //     ..._.chain( getAvailableCategoryReq.data.categories ).pluck( 'subCategories' ).flatten().pluck( 'name' ).uniq().value(),
            //     ..._.chain( getAvailableCategoryReq.data.categories ).pluck( 'subCategories' ).flatten().pluck( 'displayName' ).uniq().value(),
            //     ..._.pluck( getStaticProjectActivitiesReq.data.staticProjectActivites, 'subCategoryName' ),
            // ] );

        } else {

            getAvailableCategoryReq.data.categories.map( cat => {
                nameStrings.add( cat.name );
                nameStrings.add( cat.displayName );
            } );

            getStaticProjectActivitiesReq.data.staticProjectActivites.map( activity => {
                nameStrings.add( activity.categoryName );
            } )

            // return _.uniq( [
            //     ..._.pluck( getAvailableCategoryReq.data.categories, 'name' ),
            //     ..._.pluck( getAvailableCategoryReq.data.categories, 'displayName' ),
            //     ..._.pluck( getStaticProjectActivitiesReq.data.staticProjectActivites, 'categoryName' ),
            // ] );
        }

        return Array.from( nameStrings )

    }

    const addEmptyProjectTaxonomy: UseCategoriesHook['addEmptyProjectTaxonomy'] = ( projectId, projectVersionId, excludedTaxonomyIds = [], categoryId = null, onComplete = null ) => {

        modalActions.show(
            <AddEmptyProjectTaxonomyDialog
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                excludedTaxonomyIds={ excludedTaxonomyIds }
                categoryId={ categoryId }
                onComplete={ ( data ) => {
                    modalActions.toggle( false );
                    runIfFunction( onComplete, data );
                } }
                successToastLabel={ 'Added Categories Successfully!' }
            />, {
                // size: 'large'
            } );
    };

    const addNewProjectTaxonomy: UseCategoriesHook['addNewProjectTaxonomy'] = ( projectId, projectVersionId, categoryId = null, onComplete = null ) => {

        modalActions.show(
            <AddNewProjectTaxonomyDialog
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                categoryId={ categoryId }
                onComplete={ ( data ) => {
                    modalActions.toggle( false );
                    runIfFunction( onComplete, data );
                } }
                successToastLabel={ `Added Category Successfully!` }
            />, {
                size: 'tiny'
            } );
    };

    const updateProjectTaxonomy: UseCategoriesHook['updateProjectTaxonomy'] = ( props ) => {

        const {
                  projectId,
                  projectVersionId,
                  categoryId    = null,
                  subCategoryId = null,
                  initialValues = {},
                  noOp          = false,
                  onComplete    = null,
              } = props

        modalActions.show(
            <UpdateProjectTaxonomyDialog
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                categoryId={ categoryId }
                subCategoryId={ subCategoryId }
                initialValues={ initialValues }
                noOp={ noOp }
                onComplete={ ( data ) => {
                    modalActions.toggle( false );
                    runIfFunction( onComplete, data );
                } }
                successToastLabel={ `Updated Category Successfully!` }
            />, {
                size: 'tiny'
            } );
    };

    return {
        findCategoryDisplayInfo,
        findAllCategoryNames,
        findAllSubCategoryNames,
        findAllTaxonomyNames,
        addEmptyProjectTaxonomy,
        addNewProjectTaxonomy,
        updateProjectTaxonomy,
    }
}

export { useCategories };
